import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts'

interface LineChartData {
    sum: number
}

interface Props {
    positive: LineChartData[]
    negative: LineChartData[]
}

export const TrendChart = ({ positive, negative }: Props): JSX.Element => {
    // 月份標籤
    const months = ['去年10月', '去年11月', '去年12月', '今年1月', '今年2月', '今年3月', '今年4月', '今年5月', '今年6月', '今年7月', '今年8月', '今年9月']

    // 為 Recharts 準備資料
    const chartData = positive.map((item, index) => ({
        month: months[index] || `月份${index + 1}`,
        正向評論: item.sum,
        負向評論: negative[index]?.sum || 0
    }))

    // 找出最高點
    const maxPositiveValue = Math.max(...positive.map(item => item.sum))
    const maxNegativeValue = Math.max(...negative.map(item => item.sum))

    const maxPositiveIndex = positive.findIndex(item => item.sum === maxPositiveValue)
    const maxNegativeIndex = negative.findIndex(item => item.sum === maxNegativeValue)

    // 自定義 Tooltip
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border-2 border-gray-200 rounded-lg shadow-lg">
                    <p className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] mb-1">
                        {payload[0].payload.month}
                    </p>
                    <p className="[font-family:'Noto_Sans_TC',Helvetica] text-sm text-[#7da387]">
                        正向評論: {payload[0].value}則
                    </p>
                    <p className="[font-family:'Noto_Sans_TC',Helvetica] text-sm text-[#929084]">
                        負向評論: {payload[1]?.value || 0}則
                    </p>
                </div>
            )
        }
        return null
    }

    // 自定義 Dot - 正向評論
    const CustomPositiveDot = (props: any) => {
        const { cx, cy, index } = props
        const isMax = index === maxPositiveIndex

        if (!isMax) {
            return (
                <circle
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill="#7da387"
                    stroke="white"
                    strokeWidth={2}
                />
            )
        }

        return (
            <g>
                <circle
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill="#7da387"
                    stroke="white"
                    strokeWidth={2}
                />
                {/* 標籤背景 */}
                <image
                    x={cx - 43.5}
                    y={cy - 60}
                    width={87}
                    height={41}
                    href="/img/union-1.svg"
                />
                {/* 標籤文字 */}
                <text
                    x={cx}
                    y={cy - 40}
                    textAnchor="middle"
                    fill="white"
                    fontSize="10"
                    fontWeight="500"
                    fontFamily="Noto Sans TC, Helvetica"
                    letterSpacing="2"
                >
                    最多正向評論
                </text>
            </g>
        )
    }

    // 自定義 Dot - 負向評論
    const CustomNegativeDot = (props: any) => {
        const { cx, cy, index } = props
        const isMax = index === maxNegativeIndex

        if (!isMax) {
            return (
                <circle
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill="#929084"
                    stroke="white"
                    strokeWidth={2}
                />
            )
        }

        return (
            <g>
                <circle
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill="#929084"
                    stroke="white"
                    strokeWidth={2}
                />
                {/* 標籤背景 */}
                <image
                    x={cx - 43.5}
                    y={cy + 20}
                    width={87}
                    height={41}
                    href="/img/union.svg"
                />
                {/* 標籤文字 */}
                <text
                    x={cx}
                    y={cy + 40}
                    textAnchor="middle"
                    fill="white"
                    fontSize="10"
                    fontWeight="500"
                    fontFamily="Noto Sans TC, Helvetica"
                    letterSpacing="2"
                >
                    最多負向評論
                </text>
            </g>
        )
    }

    return (
        <div className="relative w-full h-full">
            {/* Recharts 折線圖 */}
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{ top: 60, right: 30, left: 20, bottom: 60 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                        dataKey="month"
                        tick={{ fill: '#373737', fontSize: 12, fontFamily: 'Roboto, Helvetica' }}
                        angle={-15}
                        textAnchor="end"
                        height={60}
                    />
                    <YAxis
                        tick={{ fill: '#373737', fontSize: 14, fontFamily: 'Roboto, Helvetica' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                        wrapperStyle={{ fontFamily: 'Noto Sans TC, Helvetica' }}
                        iconType="line"
                    />
                    <Line
                        type="monotone"
                        dataKey="正向評論"
                        stroke="#7da387"
                        strokeWidth={3}
                        dot={<CustomPositiveDot />}
                        activeDot={{ r: 8 }}
                        animationDuration={800}
                    />
                    <Line
                        type="monotone"
                        dataKey="負向評論"
                        stroke="#929084"
                        strokeWidth={3}
                        dot={<CustomNegativeDot />}
                        activeDot={{ r: 8 }}
                        animationDuration={800}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}
